import { ReactNode } from 'react';

/* =================== QUARTERLY REFRESH SURFACE ===================
   Snapshot 2026 (Coleman Parkes, n=1,112).
   stat1 = Q9r3 (trustworthiness), stat2 = Q12r1 (reviews & testimonials),
   stat3 = Q21r4 (unsure which installers to trust).

   REFRESH PROCEDURE
   -----------------
   Every ~3 months a new wave of the Aurora Solar Trust Signal is fielded (via Dynata) and,
   annually, a new Aurora Solar Snapshot lands (Coleman Parkes). To refresh this page, edit the
   constants in this file — the rendering components in TrustSignal.tsx read from here.

   1. Append the new quarterly point to `SIGNAL`. The chart shows four quarters — confirm with
      marketing before dropping any earlier wave.
   2. Update `YOY` if a new Snapshot wave has landed.
   3. If Snapshot subgroup breakdowns changed, update `SUBGROUP_DATA` and `NATIONAL` to match.
   4. Update the eyebrow copy ("The Aurora Solar Trust Signal 2026") in TrustSignal.tsx and the
      methodology's "Latest wave" language if the wave label shifted.
   5. Update `dateModified` in `page.tsx` to the new wave's publish date (`datePublished` stays at the first wave's date).
   6. Sanity-check the caption + methodology copy — the metric-direction language ("did not
      report a negative experience") must match whatever the new wave measures.
*/

export type StatTriple = { stat1: number; stat2: number; stat3: number };

export const NATIONAL: StatTriple = { stat1: 36, stat2: 54, stat3: 14 };

export const SUBGROUP_DATA: Record<string, StatTriple> = {
  'Baby Boomer': { stat1: 43, stat2: 50, stat3: 11 },
  'Baby Boomer|Midwest': { stat1: 49, stat2: 49, stat3: 9 },
  'Baby Boomer|South': { stat1: 44, stat2: 46, stat3: 12 },
  'Gen X': { stat1: 36, stat2: 56, stat3: 14 },
  'Gen X|Midwest': { stat1: 21, stat2: 63, stat3: 15 },
  'Gen X|South': { stat1: 40, stat2: 53, stat3: 14 },
  Millennial: { stat1: 30, stat2: 56, stat3: 17 },
  'Millennial|Midwest': { stat1: 27, stat2: 63, stat3: 23 },
  'Millennial|Northeast': { stat1: 29, stat2: 68, stat3: 11 },
  'Millennial|South': { stat1: 33, stat2: 50, stat3: 19 },
  'Millennial|West': { stat1: 30, stat2: 49, stat3: 8 },
};

export type SeriesPoint = { label: string; v: number };

export const SIGNAL: SeriesPoint[] = [
  { label: 'Aug 2025', v: 20.2 },
  { label: 'Jan 2026', v: 29.8 },
  { label: 'Apr 2026', v: 34.6 },
  { label: 'Jul 2026', v: 37.5 },
];

export const YOY: SeriesPoint[] = [
  { label: '2023', v: 22 },
  { label: '2024', v: 43 },
  { label: '2025', v: 41 },
  { label: '2026', v: 36 },
];

export const TICKER: ReactNode[] = [
  <>
    <span>71% of homeowners are at least open to going solar.</span>
    <span className="sup">1</span>
  </>,
  <>
    <span>Trust is the </span>
    <b>#2 barrier</b>
    <span> to going solar, second only to cost.</span>
    <span className="sup">1</span>
  </>,
  <>
    <span>Negative experiences with solar pros have </span>
    <b>trended down</b>
    <span> across our last four surveys.</span>
    <span className="sup">2</span>
  </>,
  <>
    <b>80%</b>
    <span> of homeowners see solar as a viable, sustainable industry.</span>
    <span className="sup">1</span>
  </>,
  <>
    <b>36%</b>
    <span> say “trustworthiness of solar companies” is a top concern.</span>
    <span className="sup">1</span>
  </>,
];
